/**
 * The one way a date, a timestamp or a weekday becomes text a person reads.
 *
 * It exists because the product never chose a format. Five surfaces called
 * `toLocaleDateString()` with no arguments and rendered whatever the reader's
 * machine happened to be configured for — `9/13/2026` on a US laptop,
 * `13/9/2026` on a Catalan one — so nobody decided it and nobody could. The
 * locale is now resolved here, once (`resolveLocale`), and `pnpm lint` refuses
 * any other caller of `Intl` under `src/`, which is what keeps "once" true.
 *
 * There are two formatters because there are two kinds of value, and collapsing
 * them is how the bug below comes back. A SQL `date` — `week.starts_on` — is a
 * calendar day with no time and no zone, and `new Date('2026-09-13')` parses it
 * as **UTC midnight**: for any reader west of UTC that renders as `12/09/2026`,
 * the day before the one the coach chose. That is exactly what the code did,
 * invisibly correct in Catalonia and wrong for a client who travels. So
 * `formatDate` pins `timeZone: 'UTC'` and the day survives; `formatMoment`
 * takes a `timestamptz` — a real instant — and renders it in the reader's own
 * zone, which is what "First signed in" and "Updated" actually mean.
 */

import { languageTag } from './language';

/**
 * The locale every rendered date, timestamp and weekday in the product is
 * formatted with. Castilian: the clients are Spanish and the coach is
 * established in Catalonia.
 *
 * **This is no longer a second decision.** It reads the product's language from
 * `src/shared/language.ts`, which is also what the words on the screen and the
 * document's `lang` attribute read. Before that module the three disagreed —
 * English strings inside `<html lang="en">` with an `es-ES` date layer
 * underneath — and nothing would have noticed if they drifted further apart.
 *
 * It is never read from the browser, the device or the operating system: what
 * the product shows is a property of the product, not of the reader's machine.
 */
export function resolveLocale(): string {
  return languageTag();
}

/**
 * `dd/mm/yyyy` with leading zeros, spelled out rather than left to the locale's
 * default. Without `2-digit`, `es-ES` renders `13/9/2026` — the right order, but
 * not the format that was asked for. `es-ES`, `ca-ES` and `en-GB` all produce
 * `13/09/2026` under these options, so the requirement survives the locale
 * dimension arriving.
 */
const DATE_OPTIONS = { day: '2-digit', month: '2-digit', year: 'numeric' } as const;

/**
 * A calendar date with no time of day — a SQL `date` such as `week.starts_on`,
 * or a date the coach typed — as `13/09/2026`, the same day for every reader in
 * every timezone.
 *
 * Takes the `YYYY-MM-DD` string itself, never a `Date` built from one, and pins
 * the formatter to UTC so the UTC-midnight parse and the UTC rendering cancel
 * out. See the module header for what happens when they do not.
 */
export function formatDate(dateOnly: string, locale: string = resolveLocale()): string {
  return new Intl.DateTimeFormat(locale, { ...DATE_OPTIONS, timeZone: 'UTC' }).format(
    new Date(`${dateOnly}T00:00:00Z`),
  );
}

/**
 * A moment in time — a `timestamptz` such as `client.first_signed_in_at` or
 * `week.updated_at` — as the calendar date of that moment **in the reader's own
 * timezone**, which is what a reader means by the day something happened.
 */
export function formatMoment(iso: string, locale: string = resolveLocale()): string {
  return new Intl.DateTimeFormat(locale, DATE_OPTIONS).format(new Date(iso));
}

/**
 * The time of day of a moment, as `18:42`, in the reader's own timezone.
 *
 * Added for one sentence: Today tells a client who has already trained *when*
 * they finished. A date would not answer that — it is the same day — and a
 * moment rendered as a date reads as an error.
 *
 * 24-hour, with a leading zero, because that is how Spain writes a time and
 * because `hourCycle` is otherwise decided by the reader's machine rather than
 * by the product. Seconds are deliberately absent: nobody finishes a session at
 * a second.
 */
const TIME_OPTIONS = { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' } as const;

export function formatTime(iso: string, locale: string = resolveLocale()): string {
  return new Intl.DateTimeFormat(locale, TIME_OPTIONS).format(new Date(iso));
}

/**
 * A week that starts on a Monday, used only to ask `Intl` for its seven day
 * names. 1 January 2024 was a Monday; the dates are arbitrary beyond that and
 * are read in UTC so the week cannot slide by a day.
 */
const MONDAY_FIRST_WEEK = [1, 2, 3, 4, 5, 6, 7].map((day) => new Date(Date.UTC(2024, 0, day)));

/**
 * The seven weekday names in the resolved locale, Monday first, in the order
 * `day_of_week` 1-7 uses in `00002`/`00003` — so `day_of_week` n is index n-1.
 *
 * Weekdays live here rather than in a screen because a weekday *is* a date
 * surface: a hardcoded English list is one a locale switch silently misses,
 * leaving Castilian dates beside an English "Monday", which is worse than
 * either being consistently wrong. Monday-first is also the Spanish and Catalan
 * convention, so no reordering logic is needed and none is written.
 */
export function weekdayNames(locale: string = resolveLocale()): string[] {
  const formatter = new Intl.DateTimeFormat(locale, { weekday: 'long', timeZone: 'UTC' });
  return MONDAY_FIRST_WEEK.map((day) => formatter.format(day));
}

/**
 * The day of the month alone — `15` — for the week strip's seven cells.
 *
 * It exists because the alternative is `day.slice(8, 10)`, and string-slicing a
 * date is exactly the kind of second date renderer this module was created to
 * prevent: it is invisible to the lint rule, it silently assumes the ISO layout,
 * and it is how a format decision gets made in a component again. A calendar day
 * in, a number a person reads out, pinned to UTC for the same reason
 * `formatDate` is — the parse and the render cancel out and the day survives
 * west of Greenwich.
 *
 * Not zero-padded: the cell is two characters wide either way, and `05` beside
 * `12` in a row of seven reads as a code rather than as a date.
 */
export function formatDayOfMonth(dateOnly: string, locale: string = resolveLocale()): string {
  return new Intl.DateTimeFormat(locale, { day: 'numeric', timeZone: 'UTC' }).format(
    new Date(`${dateOnly}T00:00:00Z`),
  );
}

/**
 * The seven weekday names abbreviated — `lun`, `mar`, … — Monday first, in the
 * same order and the same indexing as `weekdayNames()`.
 *
 * The week strip has seven columns on a 400 px phone, which is about fifty
 * pixels each: `miércoles` does not fit and `weekdayNames()` is therefore not
 * usable there. Abbreviating in the component would mean `name.slice(0, 3)`,
 * which is wrong in any language whose abbreviation is not its first three
 * letters, so the locale is asked instead — here, like every other date decision
 * in this product.
 *
 * It is never the only carrier of a day: every cell of the strip is repeated in
 * words in the list below it, and the strip itself is hidden from assistive
 * technology for exactly that reason.
 */
export function weekdayNamesShort(locale: string = resolveLocale()): string[] {
  const formatter = new Intl.DateTimeFormat(locale, { weekday: 'short', timeZone: 'UTC' });
  return MONDAY_FIRST_WEEK.map((day) => formatter.format(day));
}
